import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { Option } from '~/eds/src/types/Option';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

import { FEEDBACK_MAX_LENGTH } from '../../constants';
import { FieldClassificationGoldValue } from '../../fields/shared/FieldValue/FieldClassificationGoldValue';
import { FieldExtractionGoldValue } from '../../fields/shared/FieldValue/FieldExtractionGoldValue';
import {
  FieldClassificationValue,
  FieldExtractionValue,
  FieldValue,
} from '../../fields/types';
import { prepareClassificationOptions } from '../../fields/utils';
import { PromptModelTestCase } from '../../types/deprecated';

interface Props {
  feedback: string;
  goldValue: Nullable<FieldValue>;
  testCase: PromptModelTestCase;
  onUpdateFeedback: (value: string) => void;
  onUpdateGoldValue: (value: FieldValue) => void;
  readOnly?: boolean;
}

export const ReviewGoldValueFeedback = ({
  feedback,
  goldValue,
  readOnly,
  testCase,
  onUpdateFeedback,
  onUpdateGoldValue,
}: Props) => {
  const enableTableExtraction = useFlag(FlagType.CustomAiTableExtraction);
  const { getSearchParam } = useRouting();
  const isTableModel =
    enableTableExtraction && getSearchParam('type') === 'table';

  const intl = useIntl();
  const dispatch = useDispatch();
  const formatNumber = useFormatNumber();

  const modelVersion = useSelector(slices.xRay.selectors.selectActiveModel);

  const config = useSelector(slices.xRay.selectors.selectConfiguration);
  const classificationOptions = config.fieldClassification
    ? prepareClassificationOptions(config, modelVersion?.testCases ?? [], intl)
    : [];

  const isFeedbackValid = feedback.length <= FEEDBACK_MAX_LENGTH;
  const feedbackDisplayLength = intl.formatMessage(
    {
      id: 'ReviewGoldValueFeedback.charactersCount',
      defaultMessage: '{current}/{max} characters',
      description:
        'Character count display for feedback field. Example: current = "250", max = "500"',
    },
    {
      current: feedback.length,
      max: formatNumber(FEEDBACK_MAX_LENGTH),
    },
  );
  const feedbackFooter = isFeedbackValid ? feedbackDisplayLength : undefined;
  const feedbackError = !isFeedbackValid ? feedbackDisplayLength : undefined;

  const goldValueInput = config.fieldClassification ? (
    <FormField<FieldClassificationValue, false>
      required
      input={FieldClassificationGoldValue}
      inputProps={{
        initialValue: testCase.modelValue,
        isMulti: config.fieldClassification.isMulti,
        options: classificationOptions,
        onAddOption: config.fieldClassification.isOpenEnded
          ? (newOption: Option<string>) => {
              if (newOption) {
                dispatch(
                  slices.xRay.actions.setOptions([
                    ...classificationOptions,
                    newOption,
                  ]),
                );
              }
            }
          : undefined,
        readOnly,
      }}
      label={
        config.fieldClassification.isMulti
          ? intl.formatMessage({
              id: 'ReviewGoldValueFeedback.correctValuesMulti',
              defaultMessage: 'What are the correct values?',
              description: 'Label for multi-select correct values field',
            })
          : intl.formatMessage({
              id: 'ReviewGoldValueFeedback.correctValueSingle',
              defaultMessage: 'What is the correct value?',
              description: 'Label for single-select correct value field',
            })
      }
      name="user-feedback-correct-value"
      placeholder={
        config.fieldClassification.isMulti
          ? intl.formatMessage({
              id: 'ReviewGoldValueFeedback.placeholderMulti',
              defaultMessage: 'Select or enter correct value(s)',
              description: 'Placeholder for multi-select correct values field',
            })
          : intl.formatMessage({
              id: 'ReviewGoldValueFeedback.placeholderSingle',
              defaultMessage: 'Select or enter correct value',
              description: 'Placeholder for single-select correct value field',
            })
      }
      value={goldValue as FieldClassificationValue}
      onChange={(updatedValue) => onUpdateGoldValue(updatedValue as FieldValue)}
    />
  ) : isTableModel ? null : (
    <FormField<FieldExtractionValue, false>
      required
      input={FieldExtractionGoldValue}
      inputProps={{
        type: config.field?.type,
        readOnly,
      }}
      label={intl.formatMessage({
        id: 'ReviewGoldValueFeedback.correctValueExtraction',
        defaultMessage: 'What is the correct value?',
        description: 'Label for extraction correct value field',
      })}
      name="user-feedback-correct-value"
      placeholder={intl.formatMessage({
        id: 'ReviewGoldValueFeedback.placeholderExtraction',
        defaultMessage: 'Type correct value',
        description: 'Placeholder for extraction correct value field',
      })}
      value={goldValue as FieldExtractionValue}
      onChange={(updatedValue) => onUpdateGoldValue(updatedValue as FieldValue)}
    />
  );

  return (
    <Layout preset="form-fields" w="100%">
      {goldValueInput}
      <FormField
        optional
        description={intl.formatMessage({
          id: 'ReviewGoldValueFeedback.feedbackDescription',
          defaultMessage: 'Provide input to improve the AI.',
          description: 'Description for feedback field',
        })}
        disabled={Boolean(readOnly)}
        error={feedbackError}
        footer={feedbackFooter}
        input={TextArea}
        label={intl.formatMessage({
          id: 'ReviewGoldValueFeedback.feedbackLabel',
          defaultMessage: 'Why did the AI get it wrong?',
          description: 'Label for feedback field',
        })}
        name="user-feedback-reason-incorrect"
        placeholder={intl.formatMessage({
          id: 'ReviewGoldValueFeedback.feedbackPlaceholder',
          defaultMessage:
            'e.g. The value is usually going to be under the first clause.',
          description: 'Placeholder for feedback field',
        })}
        value={feedback}
        onChange={(updatedValue) => onUpdateFeedback(updatedValue as string)}
      />
    </Layout>
  );
};
